// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import {VNode} from 'snabbdom';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {getFocusableChildren} from 'common/util/focusable';
import {SectionItem} from 'common/components/items-menu/types';
import {IronContextRegistry} from 'common/report/context-registry';

import type {SidenavSectionItem} from '../types';
import {DropPosition} from '../constants';
import {template} from './template';
import './index.styl';

const COMPONENT_NAME = `mp-sidenav-item`;

export interface SidenavItemJsxProps {
  attrs?: {
    'is-drag-disabled'?: boolean;
    'is-selected'?: boolean;
    'is-draggable'?: boolean;
    'is-new'?: boolean;
    'label-tab-size'?: number;
    'can-drop-item'?: boolean;
    'is-nested-dashboards-enabled'?: boolean;
    'can-expand'?: boolean;
    'is-expanded'?: boolean;
    'nested-order'?: string;
  };
  on?: {
    itemDragStart?: (ev: CustomEvent<SidenavSectionItem>) => void;
    itemDragEnd?: (ev: CustomEvent<SidenavSectionItem>) => void;
    itemDrop?: (ev: CustomEvent<{from: SidenavSectionItem; to: SidenavSectionItem}>) => void;
    itemClick?: (ev: CustomEvent) => void;
    itemExpand?: (ev: CustomEvent<{itemLabel: string}>) => void;
    overflowMenuChange?: (ev: CustomEvent<{isOpen: boolean}>) => void;
    overflowMenuSelect?: (ev: CustomEvent<{item: SidenavSectionItem; menuItem: SectionItem}>) => void;
  };
  props?: {
    item?: SidenavSectionItem;
    preventDefaultNavigate?: () => boolean;
    iconRenderer?: () => VNode;
  };
}

interface SidenavItemState {
  isDragging: boolean;
  dropPosition: DropPosition | null;
  isOverflowMenuOpen: boolean;
  item?: SidenavSectionItem;
}

export class SidenavItem extends Component<
  SidenavItemState,
  SidenavItemJsxProps['attrs'],
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  public declare preventDefaultNavigate?: () => boolean;
  public declare iconRenderer?: () => VNode;

  static get attrsSchema(): AttrsSchema<SidenavItemJsxProps['attrs']> {
    return {
      'is-drag-disabled': {type: `boolean`},
      'is-selected': {type: `boolean`},
      'is-draggable': {type: `boolean`},
      'is-new': {type: `boolean`},
      'can-drop-item': {type: `boolean`},
      'label-tab-size': {type: `number`, default: 0},
      'is-nested-dashboards-enabled': {type: `boolean`},
      'can-expand': {type: `boolean`},
      'is-expanded': {type: `boolean`},
      'nested-order': {type: `string`, default: `first`},
    };
  }

  get config() {
    return {
      template,
      contexts: [`i18n` as const],
      defaultState: {
        isDragging: false,
        dropPosition: null,
        isOverflowMenuOpen: false,
        item: {label: ``, value: ``, itemId: ``},
      },
      helpers: {
        onDragStart: (ev: DragEvent) => {
          ev.dataTransfer.effectAllowed = `move`;
          ev.dataTransfer.setData(`application/json`, JSON.stringify({...this.state.item, isNested: this.isNested()}));
          this.update({isDragging: true});
          this.dispatchEvent(new CustomEvent(`itemDragStart`, {detail: this.state.item}));
        },

        onDragOver: (ev: DragEvent) => {
          ev.preventDefault();
          if (!this.attr(`can-drop-item`)) {
            ev.dataTransfer.dropEffect = `none`;
            return;
          }

          ev.dataTransfer.dropEffect = `move`;
          this.update({dropPosition: this.getDropPosition(ev)});
        },

        onDragLeave: () => {
          this.update({dropPosition: null});
        },

        onDrop: (ev: DragEvent) => {
          ev.stopPropagation();
          const data = JSON.parse(ev.dataTransfer.getData(`application/json`));
          this.dispatchEvent(
            new CustomEvent(`itemDrop`, {
              detail: {
                from: data,
                to: this.state.item,
                dropPosition: this.getDropPosition(ev),
              },
            }),
          );

          /**
           * This is a trick to update the tabindex of the next element without applying the focus state.
           * This way, the next tab will have focus on the element after the drop target.
           * Focus gets triggered at mousedown, so it's hard to avoid when using drag/drop on the same element
           */
          const nextFocusableItem = getFocusableChildren(ev.currentTarget as HTMLElement)[0];
          nextFocusableItem?.focus();
          nextFocusableItem?.blur();

          this.update({dropPosition: null, isDragging: false});
        },

        onDragEnd: () => {
          this.update({isDragging: false});
          this.dispatchEvent(new CustomEvent(`itemDragEnd`, {detail: this.state.item}));
        },

        onClick: (ev: MouseEvent) => {
          if (this.canNavigate()) {
            this.dispatchEvent(new CustomEvent(`itemClick`, {detail: this.state.item}));
          } else {
            ev.preventDefault();
            // still dispatch event to let client handle redirection/other behavior if necessary
            this.dispatchEvent(new CustomEvent(`itemClick`, {detail: this.state.item}));
          }
        },

        onCaretClick: () => {
          this.dispatchEvent(new CustomEvent(`itemExpand`, {detail: {itemId: this.state.item.itemId}}));
        },

        onOverflowMenuChange: (ev) => {
          this.update({isOverflowMenuOpen: ev.detail.isOpen});
          this.dispatchEvent(new CustomEvent(`overflowMenuChange`, {detail: {isOpen: ev.detail.isOpen}}));
        },

        onOverflowMenuSelect: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`overflowMenuSelect`, {
              detail: {...ev.detail, item: this.state.item, menuItem: ev.detail.item},
            }),
          );

          // Don't want to persist focus state
          ev.target.blur();
        },

        hasOverflowMenu: () => {
          return !!this.state.item.overflowMenuSections?.length;
        },
      },
    };
  }

  get i18n() {
    return this.getContext(`i18n`);
  }

  get t() {
    return this.i18n.t;
  }

  get print() {
    return this.i18n.print;
  }

  private isNested() {
    return this.attr(`label-tab-size`) !== 0;
  }

  private getDropPosition(ev: DragEvent) {
    if (!this.attr(`is-nested-dashboards-enabled`)) {
      return this.getDropPositionWithoutNesting(ev);
    }

    // We don't currently support re-ordering of nested items
    if (this.isNested()) {
      return DropPosition.On;
    }

    const dropTarget = ev.currentTarget as HTMLElement;
    const rect = dropTarget.getBoundingClientRect();
    if (ev.clientY - (rect.top + rect.height / 3) < 0) {
      return DropPosition.Above;
    } else if (
      ev.clientY - (rect.bottom - rect.height / 3) > 0 &&
      !(this.attr(`is-expanded`) && this.attr(`can-expand`))
    ) {
      return DropPosition.Below;
    }
    return DropPosition.On;
  }

  // TODO @carlos remove after nester-boards is GA
  private getDropPositionWithoutNesting(ev: DragEvent) {
    const dropTarget = ev.currentTarget as HTMLElement;
    const rect = dropTarget.getBoundingClientRect();
    return ev.clientY - (rect.top + rect.height / 2) < 0 ? DropPosition.Above : DropPosition.Below;
  }

  private canNavigate() {
    return !this.preventDefaultNavigate || !this.preventDefaultNavigate();
  }

  public set item(item: SidenavSectionItem) {
    this.update({item});
  }
}

export const SidenavItemEl = defineAndMakeJsxFactory(COMPONENT_NAME, SidenavItem);
export const SidenavItemReact = panel2react(COMPONENT_NAME, SidenavItem);
